// Copyright (C) 2026, Lifefinder Systems International AB, all rights reserved.
import React, { useEffect, useState } from 'react';

import { solverAPI, SolverOption } from '../../../utils/apiClient';
import { Button } from '../../common/Button';

interface Props {
  inline?: boolean;
  onClose: () => void;
}

interface TestDevice {
  deveui: string;
  name: string;
  description: string;
  device_category: string;
  test_mode: number | null;
}

export const SolverSettings: React.FC<Props> = ({ inline, onClose }) => {
  const [currentSolver, setCurrentSolver] = useState<string>('');
  const [selected, setSelected] = useState<string>('');
  const [availableSolvers, setAvailableSolvers] = useState<SolverOption[]>([]);

  const [testSolverA, setTestSolverA] = useState<string>('');
  const [testSolverB, setTestSolverB] = useState<string>('');
  const [currentTestA, setCurrentTestA] = useState<string | null>(null);
  const [currentTestB, setCurrentTestB] = useState<string | null>(null);
  const [testEnabled, setTestEnabled] = useState(false);

  const [devices, setDevices] = useState<TestDevice[]>([]);
  const [selectedDeveuis, setSelectedDeveuis] = useState<Set<string>>(new Set());
  const [loadingDevices, setLoadingDevices] = useState(false);
  const [savingDevices, setSavingDevices] = useState(false);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingTest, setSavingTest] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    load();
  }, []);

  const loadDevices = async () => {
    try {
      setLoadingDevices(true);
      const data = await solverAPI.getTestDevices();
      if (!data.success) return;
      // Sort once at load time: saved test-mode devices first, rest in original order.
      // Intentionally static — the list does not jump when the user toggles checkboxes.
      const sorted = [
        ...data.devices.filter((d) => d.test_mode),
        ...data.devices.filter((d) => !d.test_mode),
      ];
      setDevices(sorted);
      setSelectedDeveuis(new Set(sorted.filter((d) => d.test_mode).map((d) => d.deveui)));
    } catch (_) {
    } finally {
      setLoadingDevices(false);
    }
  };

  const load = async () => {
    try {
      setLoading(true);
      setError(null);
      const [main, test] = await Promise.all([solverAPI.getSettings(), solverAPI.getTestSolvers()]);
      if (!main.success) throw new Error((main as any).error || 'Failed to load');
      setCurrentSolver(main.currentSolver);
      setSelected(main.currentSolver);
      setAvailableSolvers(main.availableSolvers);

      const a = test.test_solver_a || '';
      const b = test.test_solver_b || '';
      setCurrentTestA(test.test_solver_a);
      setCurrentTestB(test.test_solver_b);
      setTestEnabled(!!(test.test_solver_a && test.test_solver_b));
      setTestSolverA(a || (main.availableSolvers[0]?.id ?? ''));
      setTestSolverB(b || (main.availableSolvers[0]?.id ?? ''));

      if (test.test_solver_a && test.test_solver_b) {
        await loadDevices();
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  };

  const hasChanges = selected !== currentSolver;
  const hasTestChanges =
    (testEnabled &&
      (testSolverA !== (currentTestA ?? '') || testSolverB !== (currentTestB ?? ''))) ||
    (!testEnabled && (currentTestA !== null || currentTestB !== null)) ||
    (testEnabled && (currentTestA === null || currentTestB === null));

  const handleSave = async () => {
    if (!hasChanges) return;
    const solverName = availableSolvers.find((s) => s.id === selected)?.name ?? selected;
    if (
      !window.confirm(
        `Switch solver to "${solverName}"? Takes effect immediately for new positions.`
      )
    )
      return;
    try {
      setSaving(true);
      const result = await solverAPI.setSolver(selected);
      if (!result.success) throw new Error((result as any).error || 'Save failed');
      await load();
    } catch (e) {
      alert(`Save failed: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveTest = async () => {
    if (!hasTestChanges) return;
    const a = testEnabled ? testSolverA : null;
    const b = testEnabled ? testSolverB : null;
    if (testEnabled && a === b) {
      alert('Solver A and Solver B must be different.');
      return;
    }
    try {
      setSavingTest(true);
      const result = await solverAPI.setTestSolvers(a, b);
      if (!result.success) throw new Error((result as any).error || 'Save failed');
      await load();
    } catch (e) {
      alert(`Save failed: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setSavingTest(false);
    }
  };

  const handleApplyDevices = async (deveuis: string[]) => {
    try {
      setSavingDevices(true);
      await solverAPI.setTestDevices(deveuis);
      await loadDevices();
    } catch (e) {
      alert(`Save failed: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setSavingDevices(false);
    }
  };

  const toggleDevice = (deveui: string) => {
    setSelectedDeveuis((prev) => {
      const next = new Set(prev);
      if (next.has(deveui)) next.delete(deveui);
      else next.add(deveui);
      return next;
    });
  };

  const deviceSelectionChanged = (() => {
    const saved = new Set(devices.filter((d) => d.test_mode).map((d) => d.deveui));
    if (saved.size !== selectedDeveuis.size) return true;
    for (const id of selectedDeveuis) if (!saved.has(id)) return true;
    return false;
  })();

  const containerStyle: React.CSSProperties = inline
    ? {}
    : {
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        background: 'rgba(0,0,0,0.6)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'flex-start',
        paddingTop: 'max(20px, 5vh)',
        zIndex: 5000,
      };

  if (loading) {
    return (
      <div style={containerStyle}>
        <div style={{ padding: '3vmin', fontSize: '2vmin', color: '#666' }}>Loading...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={containerStyle} onClick={inline ? undefined : onClose}>
        <div
          style={{ background: 'white', padding: '3vmin', borderRadius: '1vmin' }}
          onClick={inline ? undefined : (e) => e.stopPropagation()}
        >
          <p style={{ color: '#f44' }}>{error}</p>
          <Button
            text="Close"
            onClick={onClose}
            style={{
              background: '#ccc',
              color: '#333',
              padding: '1vmin 2vmin',
              fontSize: '1.6vmin',
            }}
          />
        </div>
      </div>
    );
  }

  const testModeActive = !!(currentTestA && currentTestB);

  return (
    <div style={containerStyle} onClick={inline ? undefined : onClose}>
      <div
        style={{
          background: 'white',
          borderRadius: '1vmin',
          ...(inline ? {} : { maxWidth: '520px', width: '90%', maxHeight: 'min(90vh, 85dvh)' }),
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          boxShadow: inline ? 'none' : '0 8px 16px rgba(0,0,0,0.3)',
        }}
        onClick={inline ? undefined : (e) => e.stopPropagation()}
      >
        <div
          style={{
            ...(inline ? {} : { overflowY: 'scroll', flex: 1 }),
            padding: '2.5vmin',
            overscrollBehavior: 'contain',
          }}
        >
          {/* Header */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '2vmin',
              borderBottom: '2px solid #ddd',
              paddingBottom: '1.5vmin',
            }}
          >
            <h2 style={{ margin: 0, fontSize: '2.5vmin', color: '#333' }}>Solver Settings</h2>
            {!inline && (
              <Button
                text="✕"
                onClick={onClose}
                style={{
                  background: '#f44',
                  color: 'white',
                  fontSize: '2vmin',
                  padding: '0.5vmin 1vmin',
                  borderRadius: '0.3vmin',
                }}
              />
            )}
          </div>

          {/* Info banner */}
          <div
            style={{
              background: '#e3f2fd',
              padding: '1.5vmin',
              borderRadius: '0.5vmin',
              marginBottom: '2vmin',
              fontSize: '1.5vmin',
              border: '1px solid #2196F3',
              color: '#1565C0',
            }}
          >
            The solver determines which positioning service is used for new WiFi and GNSS positions.
            Changes take effect immediately — previously stored positions are not affected.
          </div>

          {/* Active Solver */}
          <div
            style={{
              background: '#f9f9f9',
              padding: '1.5vmin',
              borderRadius: '0.5vmin',
              border: '1px solid #ddd',
            }}
          >
            <h3 style={{ margin: '0 0 1vmin 0', fontSize: '2vmin', color: '#333' }}>
              Active Solver
            </h3>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '1vmin',
                padding: '1vmin',
                background: 'white',
                borderRadius: '0.3vmin',
                border: '1px solid #e0e0e0',
              }}
            >
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: '1.6vmin', fontWeight: 'bold' }}>Positioning solver</div>
                <div style={{ fontSize: '1.3vmin', color: '#888' }}>account.solver</div>
              </div>
              <select
                value={selected}
                onChange={(e) => setSelected(e.target.value)}
                style={{
                  fontSize: '1.6vmin',
                  padding: '0.5vmin 1vmin',
                  border: hasChanges ? '2px solid #2196F3' : '1px solid #ccc',
                  borderRadius: '0.3vmin',
                  minWidth: '180px',
                }}
              >
                {availableSolvers.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
            </div>
            {selected !== currentSolver && (
              <div
                style={{
                  marginTop: '1vmin',
                  fontSize: '1.4vmin',
                  color: '#888',
                  fontStyle: 'italic',
                }}
              >
                Current:{' '}
                {availableSolvers.find((s) => s.id === currentSolver)?.name ?? currentSolver}
              </div>
            )}
          </div>

          {/* Test Mode */}
          <div style={{ marginTop: '2vmin' }}>
            <div
              style={{
                background: '#fff8e1',
                padding: '1.5vmin',
                borderRadius: '0.5vmin',
                border: '1px solid #FFC107',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '1vmin',
                  marginBottom: '1vmin',
                }}
              >
                <h3 style={{ margin: 0, fontSize: '2vmin', color: '#333', flex: 1 }}>Test Mode</h3>
                <label
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5vmin',
                    fontSize: '1.6vmin',
                    cursor: 'pointer',
                  }}
                >
                  <input
                    type="checkbox"
                    checked={testEnabled}
                    onChange={(e) => setTestEnabled(e.target.checked)}
                    style={{ width: '1.6vmin', height: '1.6vmin' }}
                  />
                  Enabled
                </label>
              </div>

              <div style={{ fontSize: '1.4vmin', color: '#555', marginBottom: '1.2vmin' }}>
                Devices with <strong>test mode</strong> enabled run both solvers in parallel and
                write only to <code>test_positioning</code> — the live position feed is unaffected.
              </div>

              {testEnabled && (
                <>
                  {[
                    { label: 'Solver A', value: testSolverA, onChange: setTestSolverA },
                    { label: 'Solver B', value: testSolverB, onChange: setTestSolverB },
                  ].map(({ label, value, onChange }) => (
                    <div
                      key={label}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '1vmin',
                        padding: '0.8vmin',
                        background: 'white',
                        borderRadius: '0.3vmin',
                        border: '1px solid #e0e0e0',
                        marginBottom: '0.6vmin',
                      }}
                    >
                      <div style={{ flex: 1, fontSize: '1.6vmin', fontWeight: 'bold' }}>
                        {label}
                      </div>
                      <select
                        value={value}
                        onChange={(e) => onChange(e.target.value)}
                        style={{
                          fontSize: '1.6vmin',
                          padding: '0.4vmin 0.8vmin',
                          border: '1px solid #ccc',
                          borderRadius: '0.3vmin',
                          minWidth: '180px',
                        }}
                      >
                        {availableSolvers.map((s) => (
                          <option key={s.id} value={s.id}>
                            {s.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  ))}
                  {testSolverA === testSolverB && (
                    <div style={{ fontSize: '1.3vmin', color: '#f44', marginTop: '0.5vmin' }}>
                      Solver A and Solver B must be different.
                    </div>
                  )}
                </>
              )}

              {hasTestChanges && (
                <div
                  style={{
                    marginTop: '1vmin',
                    fontSize: '1.4vmin',
                    color: '#e65100',
                    fontStyle: 'italic',
                  }}
                >
                  Unsaved test mode changes
                </div>
              )}

              <Button
                text={savingTest ? 'Saving...' : 'Save Test Mode'}
                onClick={handleSaveTest}
                disabled={
                  savingTest || !hasTestChanges || (testEnabled && testSolverA === testSolverB)
                }
                style={{
                  marginTop: '1vmin',
                  width: '100%',
                  background:
                    hasTestChanges && !(testEnabled && testSolverA === testSolverB)
                      ? '#FF9800'
                      : '#ccc',
                  color: 'white',
                  fontSize: '1.6vmin',
                  padding: '1vmin 2vmin',
                  border: 'none',
                  borderRadius: '0.5vmin',
                  cursor:
                    hasTestChanges && !(testEnabled && testSolverA === testSolverB) && !savingTest
                      ? 'pointer'
                      : 'not-allowed',
                  opacity:
                    hasTestChanges && !(testEnabled && testSolverA === testSolverB) && !savingTest
                      ? 1
                      : 0.6,
                }}
              />
            </div>
          </div>

          {/* Test Device List — only shown when test mode is saved and active */}
          {testModeActive && (
            <div style={{ marginTop: '2vmin' }}>
              <div
                style={{
                  background: '#f3e5f5',
                  padding: '1.5vmin',
                  borderRadius: '0.5vmin',
                  border: '1px solid #9C27B0',
                }}
              >
                <h3 style={{ margin: '0 0 1vmin 0', fontSize: '2vmin', color: '#333' }}>
                  Test Devices
                </h3>
                <div style={{ fontSize: '1.4vmin', color: '#555', marginBottom: '1vmin' }}>
                  Select devices to run through the test solver pair. Selected devices write only to{' '}
                  <code>test_positioning</code>.
                </div>

                {loadingDevices ? (
                  <div style={{ fontSize: '1.4vmin', color: '#888', padding: '1vmin 0' }}>
                    Loading devices...
                  </div>
                ) : devices.length === 0 ? (
                  <div style={{ fontSize: '1.4vmin', color: '#888', padding: '1vmin 0' }}>
                    No active devices found.
                  </div>
                ) : (
                  <div
                    style={{
                      border: '1px solid #e0e0e0',
                      borderRadius: '0.3vmin',
                      background: 'white',
                      marginBottom: '1vmin',
                    }}
                  >
                    <label
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '1vmin',
                        padding: '0.8vmin 1vmin',
                        cursor: 'pointer',
                        borderBottom: '2px solid #e0e0e0',
                        background: '#fafafa',
                        fontWeight: 'bold',
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={
                          devices.length > 0 && devices.every((d) => selectedDeveuis.has(d.deveui))
                        }
                        ref={(el) => {
                          if (el)
                            el.indeterminate =
                              selectedDeveuis.size > 0 &&
                              !devices.every((d) => selectedDeveuis.has(d.deveui));
                        }}
                        onChange={(e) =>
                          setSelectedDeveuis(
                            e.target.checked ? new Set(devices.map((d) => d.deveui)) : new Set()
                          )
                        }
                        style={{ width: '1.4vmin', height: '1.4vmin', flexShrink: 0 }}
                      />
                      <span style={{ fontSize: '1.5vmin' }}>
                        {selectedDeveuis.size === 0
                          ? 'Select all'
                          : `${selectedDeveuis.size} / ${devices.length} selected`}
                      </span>
                    </label>
                    {/* Device rows — same compact height as Solver A/B rows (padding 0.8vmin) */}
                    <div style={{ maxHeight: '40vmin', overflowY: 'auto' }}>
                      {devices.map((d) => (
                        <label
                          key={d.deveui}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '1vmin',
                            padding: '0.8vmin 1vmin',
                            cursor: 'pointer',
                            borderBottom: '1px solid #f0f0f0',
                            background: selectedDeveuis.has(d.deveui) ? '#f3e5f5' : 'white',
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={selectedDeveuis.has(d.deveui)}
                            onChange={() => toggleDevice(d.deveui)}
                            style={{ width: '1.4vmin', height: '1.4vmin', flexShrink: 0 }}
                          />
                          {/* Name (or description) + EUI on one line, category right-aligned */}
                          <span style={{ flex: 1, fontSize: '1.6vmin', fontWeight: 'bold' }}>
                            {d.description || d.name}
                            <span
                              style={{
                                fontSize: '1.3vmin',
                                color: '#888',
                                marginLeft: '0.6vmin',
                                fontWeight: 'normal',
                              }}
                            >
                              {d.name}
                            </span>
                          </span>
                          <span style={{ fontSize: '1.2vmin', color: '#999' }}>
                            {d.device_category}
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}

                <div style={{ display: 'flex', gap: '1vmin' }}>
                  <Button
                    text={
                      savingDevices ? 'Saving...' : `Set selected as test (${selectedDeveuis.size})`
                    }
                    onClick={() => handleApplyDevices(Array.from(selectedDeveuis))}
                    disabled={savingDevices || !deviceSelectionChanged}
                    style={{
                      flex: 1,
                      background: deviceSelectionChanged && !savingDevices ? '#9C27B0' : '#ccc',
                      color: 'white',
                      fontSize: '1.5vmin',
                      padding: '0.9vmin 1.5vmin',
                      border: 'none',
                      borderRadius: '0.5vmin',
                      cursor: deviceSelectionChanged && !savingDevices ? 'pointer' : 'not-allowed',
                      opacity: deviceSelectionChanged && !savingDevices ? 1 : 0.6,
                    }}
                  />
                  <Button
                    text="Clear all"
                    onClick={() => {
                      setSelectedDeveuis(new Set());
                      handleApplyDevices([]);
                    }}
                    disabled={savingDevices || devices.every((d) => !d.test_mode)}
                    style={{
                      background: devices.some((d) => d.test_mode) ? '#e53935' : '#ccc',
                      color: 'white',
                      fontSize: '1.5vmin',
                      padding: '0.9vmin 1.5vmin',
                      border: 'none',
                      borderRadius: '0.5vmin',
                      cursor:
                        devices.some((d) => d.test_mode) && !savingDevices
                          ? 'pointer'
                          : 'not-allowed',
                      opacity: devices.some((d) => d.test_mode) && !savingDevices ? 1 : 0.6,
                    }}
                  />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer buttons */}
        <div
          style={{
            flexShrink: 0,
            padding: '1.5vmin 2.5vmin',
            borderTop: '1px solid #ddd',
            background: 'white',
          }}
        >
          {hasChanges && (
            <div
              style={{
                marginBottom: '1vmin',
                fontSize: '1.4vmin',
                color: '#2196F3',
                textAlign: 'center',
                fontStyle: 'italic',
              }}
            >
              Unsaved changes
            </div>
          )}
          <div style={{ display: 'flex', gap: '1vmin' }}>
            <Button
              text={saving ? 'Saving...' : 'Save Changes'}
              onClick={handleSave}
              disabled={saving || !hasChanges}
              style={{
                flex: 1,
                background: hasChanges ? '#4CAF50' : '#ccc',
                color: 'white',
                fontSize: '1.8vmin',
                padding: '1.2vmin 2vmin',
                border: 'none',
                borderRadius: '0.5vmin',
                cursor: hasChanges && !saving ? 'pointer' : 'not-allowed',
                opacity: hasChanges && !saving ? 1 : 0.6,
              }}
            />
            {!inline && (
              <Button
                text="Cancel"
                onClick={onClose}
                disabled={saving}
                style={{
                  flex: 1,
                  background: '#ccc',
                  color: '#333',
                  fontSize: '1.8vmin',
                  padding: '1.2vmin 2vmin',
                  borderRadius: '0.5vmin',
                }}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SolverSettings;
